import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AnalyticsResultDto,
  Attribute,
  Entry_String_AttributeData_String,
  MdmDeviceDto,
  MdmObjectDto,
  useUpdateTableDetailsForUserMutation,
} from "gql/generated/types";
import { useDisplayTimezone } from "hooks/useTimezone";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { CustomRowData, MdmTableName, TableColumn } from "../utils/types";
import { apolloErrorToaster, renderCustomFieldValue, successToaster } from "../utils/utils";
import useProjectContext from "./useProjectContext";
import useUserTableDetails from "./useUserTableDetails";

type CustomFieldType = Record<
  string,
  {
    value: string | null | undefined;
    id: string | null | undefined;
    title: string | undefined;
  }
>;

const useCustomColumns = <T>(tableName: MdmTableName, columns: TableColumn<T>[], data: T[], rows: number) => {
  const { userSettings, project, projectId } = useProjectContext();
  const tableDetails = useUserTableDetails(userSettings, tableName);
  const displayTz = useDisplayTimezone();

  const initCustomColumns = useMemo(() => {
    const columnList = [...(tableDetails?.columnOrders ?? [])]
      .sort((a, b) => a!.order - b!.order)
      .map(r => r!.name);
    if (userSettings && columnList.length > 0) {
      return columnList;
    }
    return columns?.map(col => col.columnKey) || [];
  }, [projectId]);

  const [customColumns, setCustomColumns] = useState<string[]>(initCustomColumns);

  const [updateUserSettings, { loading: updateLoading }] = useUpdateTableDetailsForUserMutation({
    onCompleted: () => successToaster(T("alert.columns-saved-successfully")),
    onError: apolloErrorToaster,
  });

  useEffect(() => {
    setCustomColumns(initCustomColumns);
  }, [initCustomColumns]);

  const handleColumnSave = useCallback(() => {
    updateUserSettings({
      variables: {
        projectId: projectId!,
        tableDetails: {
          table: tableName,
          rows,
          columnOrders: customColumns.map((col, i) => ({
            name: col,
            order: i,
          })),
        },
      },
    });
  }, [customColumns, projectId, tableName, rows, updateUserSettings]);

  const customCols = useMemo(() => {
    const deviceCustomFields = (project?.mdmProjectById.configuration?.deviceConfiguration.customFields ??
      []) as Attribute[];
    const objectCustomFields = (project?.mdmProjectById.configuration?.objectConfiguration.customFields ??
      []) as Attribute[];
    switch (tableName) {
      case "Analytics":
        return [...objectCustomFields, ...deviceCustomFields];
      case "Devices":
      case "ChildDevices":
      case "AnalyticsByObject":
      case "DevicesByObject":
        return [...deviceCustomFields];
      case "Objects":
      case "Jobs":
      case "JobsByObject":
        return [...objectCustomFields];
      case "Child":
        return (project?.mdmProjectById.configuration?.childrenConfiguration.find(
          c => c?.childType === tableName.toUpperCase()
        )?.customFields ?? []) as Attribute[];
      default:
        return [];
    }
  }, [
    project?.mdmProjectById.configuration?.childrenConfiguration,
    project?.mdmProjectById.configuration?.deviceConfiguration.customFields,
    project?.mdmProjectById.configuration?.objectConfiguration.customFields,
    tableName,
  ]);

  const mapCols = useMemo(
    () =>
      [
        ...columns,
        ...(customCols?.map(col => ({
          field: `${col?.id}.value`,
          header: T((col?.title ?? "") as StringKey, { defaultValue: col?.title }),
          body: (rowData: CustomRowData) =>
            renderCustomFieldValue(rowData, col?.id as keyof CustomRowData, customCols, displayTz),
          sortable: true,
          filter: tableName === "DevicesByObject" || tableName === "Jobs" || tableName === "JobsByObject",
          columnKey: col?.id,
        })) ?? []),
      ] as TableColumn<T>[],
    [columns, customCols, tableName]
  );

  useEffect(() => {
    if (!mapCols) return;
    const mapColKeys = mapCols.map(col => col.columnKey);
    // Only keep columns that exist in mapCols
    const filtered = customColumns.filter(col => mapColKeys.includes(col));

    if (filtered.length !== customColumns.length) {
      setCustomColumns(filtered);
    }
  }, [mapCols, customColumns]);

  const formattedColumns = useMemo(() => {
    return mapCols
      .filter(col => customColumns.includes(col.columnKey))
      .sort((a, b) => {
        const indexA = customColumns.indexOf(a.columnKey);
        const indexB = customColumns.indexOf(b.columnKey);
        if (indexA === -1 && indexB === -1) return a.columnKey.localeCompare(b.columnKey);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
  }, [customColumns, mapCols]);

  const formatObjectFields = useMemo(
    () =>
      data.reduce((acc, obj) => {
        let customFields: Entry_String_AttributeData_String[] = [];
        const analytics = obj as AnalyticsResultDto;
        switch (tableName) {
          case "Analytics":
            customFields = [
              ...(analytics.mdmObject?.customFields ?? []),
              ...(analytics.mdmDevice?.customFields ?? []),
            ] as Entry_String_AttributeData_String[];
            break;
          case "AnalyticsByObject":
          case "DevicesByObject":
            customFields = [
              ...(analytics.mdmDevice?.customFields ?? []),
            ] as Entry_String_AttributeData_String[];
            break;
          case "Jobs":
          case "JobsByObject":
            customFields = [
              ...(analytics.mdmObject?.customFields ?? []),
            ] as Entry_String_AttributeData_String[];
            break;
          case "Devices":
          case "ChildDevices":
          case "Objects":
          case "Child":
            customFields = [
              ...((obj as MdmDeviceDto | MdmObjectDto).customFields ?? []),
            ] as Entry_String_AttributeData_String[];
            break;
          default:
            break;
        }

        const objFields = customFields?.reduce((accObj, field) => {
          const newObj = {
            value: field?.value?.value,
            id: field?.key,
            title: customCols.find(col => col?.id === field?.key)?.title,
          };
          return {
            ...accObj,
            [field?.key!]: newObj,
          };
        }, {} as CustomFieldType);

        acc.push({
          ...obj,
          ...objFields,
        });

        return acc;
      }, [] as T[]),
    [data, customCols, tableName]
  );

  return [
    customColumns,
    setCustomColumns,
    formattedColumns,
    mapCols,
    formatObjectFields,
    handleColumnSave,
    updateLoading,
  ] as const;
};

export default useCustomColumns;
